import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  useToast,
  chakra,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { MdDelete } from "react-icons/md";
import useAxios from "axios-hooks";
import logger from "infra/logger";

// Modal component for editing property owner details
const PropertyOwnerDetailsModal = ({
  isOpen,
  onClose,
  powner,
  station,
  refresh,
}) => {
  const bgColor = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue(
    "rgba(245, 125, 54, 0.2)",
    "rgba(245, 125, 54, 0.3)"
  );
  const textColor = useColorModeValue("gray.700", "white");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const tableBgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const hoverBgColor = useColorModeValue(
    "rgba(245, 245, 250, 0.6)",
    "rgba(45, 55, 72, 0.3)"
  );
  const toast = useToast();

  // Get property owner from parent component
  const [propertyOwner, setPropertyOwner] = useState(null);
  const [owners, setOwners] = useState(null);

  const [{ data, loading, error }, getPropertyOwnersForStation] = useAxios(
    {
      method: "GET",
      url: `/api/user/propertyOwners/tenant/${station?.TenantId}`,
    },
    { manual: true }
  );

  const [
    { data: deleteData, loading: deleteLoading, error: deleteError },
    executeDeleteAssignment,
  ] = useAxios(
    {
      method: "DELETE",
      url: `/api/userStationAccess/delete/powner/${powner?.AssignmentId}`,
    },
    { manual: true }
  );

  const [
    { data: assignData, loading: assignLoading, error: assignError },
    executeAssignAssignment,
  ] = useAxios(
    {
      method: "POST",
      url: `/api/userStationAccess/assign/powner`,
    },
    { manual: true }
  );

  // Handle delete property owner
  const handleDeletePropertyOwner = async () => {
    try {
      // Show confirmation dialog
      await executeDeleteAssignment();
      toast({
        title: "Property owner removed",
        description:
          "The property owner has been successfully removed from this station.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });

      // Close modal and refresh data
      onClose();
      refresh();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove property owner. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      logger.error("Error removing property owner:", error);
    }
  };

  const getOwners = async () => {
    try {
      const response = await getPropertyOwnersForStation();
      const ownerData = response.data.data;
      setOwners(ownerData);
    } catch (error) {
      logger.log(error);
      toast({
        title: "Error",
        description: "Failed to fetch property owners. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  // Handle assign property owner
  const handleAssignPropertyOwner = async (ownerId) => {
    try {
      // Implement API call to assign property owner
      let body = {};
      body.UserId = ownerId;
      body.ChargingStationId = station.Id;
      await executeAssignAssignment({ data: body });

      toast({
        title: "Property owner assigned",
        description:
          "The property owner has been successfully assigned to this station.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });

      // Close modal and refresh data
      onClose();
      refresh();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to assign property owner. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      logger.error("Error assigning property owner:", error);
    }
  };

  useEffect(() => {
    // Set property owner from parent component when modal opens
    if (isOpen && powner) {
      setPropertyOwner(powner);
    } else {
      setPropertyOwner(null);
      setOwners(null);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        as={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        bg={bgColor}
        borderRadius="xl"
        border={`1px solid ${borderColor}`}
        boxShadow="0 8px 32px rgba(0, 0, 0, 0.05)"
        backdropFilter="blur(12px)"
        overflow="hidden"
        position="relative"
        _before={{
          content: '""',
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          height: "2px",
          background:
            "linear-gradient(90deg, rgba(245, 125, 54, 0.1), rgba(245, 125, 54, 0.6), rgba(245, 125, 54, 0.1))",
          zIndex: 1,
        }}
      >
        <ModalHeader
          borderBottom="1px solid"
          borderColor={borderColor}
          color="#f57d36"
          fontWeight="600"
        >
          Property Owner Details
        </ModalHeader>
        <ModalCloseButton color="#f57d36" />
        <ModalBody py={6}>
          {propertyOwner ? (
            <Box>
              <Flex direction="column" gap={4}>
                <Flex direction="row" justify="space-between">
                  <Box>
                    <Text fontSize="sm" color={labelColor} mb={1}>
                      Name
                    </Text>
                    <Text fontSize="md" fontWeight="500" color={textColor}>
                      {propertyOwner.FirstName} {propertyOwner.LastName}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={labelColor} mb={1}>
                      Role
                    </Text>
                    <Text fontSize="md" fontWeight="500" color={textColor}>
                      {propertyOwner.Role}
                    </Text>
                  </Box>
                </Flex>

                <Box>
                  <Text fontSize="sm" color={labelColor} mb={1}>
                    Email
                  </Text>
                  <Text fontSize="md" fontWeight="500" color={textColor}>
                    {propertyOwner.Email}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="sm" color={labelColor} mb={1}>
                    Mobile Number
                  </Text>
                  <Text fontSize="md" fontWeight="500" color={textColor}>
                    {propertyOwner.MobileNumber}
                  </Text>
                </Box>

                <Box mt={4}>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    size="sm"
                    leftIcon={<Icon as={MdDelete} />}
                    onClick={handleDeletePropertyOwner}
                    _hover={{
                      bg: "rgba(229, 62, 62, 0.1)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(229, 62, 62, 0.15)",
                    }}
                  >
                    Remove Property Owner
                  </Button>
                </Box>
              </Flex>
            </Box>
          ) : (
            <Box>
              {owners && owners.length > 0 ? (
                <Box>
                  <Text color={textColor} fontSize="md" fontWeight="500" mb={4}>
                    Available Property Owners
                  </Text>
                  <Box
                    borderRadius="xl"
                    overflow="hidden"
                    bg={tableBgColor}
                    backdropFilter="blur(7px)"
                    border="1px solid"
                    borderColor={borderColor}
                    boxShadow="0 8px 32px rgba(0, 0, 0, 0.05)"
                  >
                    <Box overflowX="auto">
                      <chakra.table
                        width="100%"
                        style={{ borderCollapse: "collapse" }}
                      >
                        <chakra.thead>
                          <chakra.tr>
                            <chakra.th
                              padding="12px 16px"
                              textAlign="left"
                              borderBottom={`1px solid ${borderColor}`}
                              color={textColor}
                              fontWeight="600"
                              fontSize="14px"
                            >
                              Name
                            </chakra.th>
                            <chakra.th
                              padding="12px 16px"
                              textAlign="left"
                              borderBottom={`1px solid ${borderColor}`}
                              color={textColor}
                              fontWeight="600"
                              fontSize="14px"
                            >
                              Email
                            </chakra.th>
                            <chakra.th
                              padding="12px 16px"
                              textAlign="left"
                              borderBottom={`1px solid ${borderColor}`}
                              color={textColor}
                              fontWeight="600"
                              fontSize="14px"
                            >
                              Mobile
                            </chakra.th>
                            <chakra.th
                              padding="12px 16px"
                              textAlign="center"
                              borderBottom={`1px solid ${borderColor}`}
                              color={textColor}
                              fontWeight="600"
                              fontSize="14px"
                            >
                              Action
                            </chakra.th>
                          </chakra.tr>
                        </chakra.thead>
                        <chakra.tbody>
                          {owners.map((owner) => (
                            <chakra.tr
                              key={owner.Id}
                              transition="all 0.2s ease"
                              _hover={{
                                bg: hoverBgColor,
                              }}
                            >
                              <chakra.td
                                padding="12px 16px"
                                borderBottom={`1px solid ${borderColor}`}
                                color={textColor}
                                fontSize="14px"
                              >
                                {owner.FirstName} {owner.LastName}
                              </chakra.td>
                              <chakra.td
                                padding="12px 16px"
                                borderBottom={`1px solid ${borderColor}`}
                                color={textColor}
                                fontSize="14px"
                              >
                                {owner.Email}
                              </chakra.td>
                              <chakra.td
                                padding="12px 16px"
                                borderBottom={`1px solid ${borderColor}`}
                                color={textColor}
                                fontSize="14px"
                              >
                                {owner.MobileNumber}
                              </chakra.td>
                              <chakra.td
                                padding="12px 16px"
                                borderBottom={`1px solid ${borderColor}`}
                                textAlign="center"
                              >
                                <Button
                                  isLoading={loading}
                                  borderRadius="md"
                                  size="sm"
                                  bg="#f57d36"
                                  color="white"
                                  onClick={() =>
                                    handleAssignPropertyOwner(owner.Id)
                                  }
                                  _hover={{
                                    bg: "rgba(245, 125, 54, 0.8)",
                                    transform: "translateY(-2px)",
                                    boxShadow:
                                      "0 4px 12px rgba(245, 125, 54, 0.3)",
                                  }}
                                >
                                  Assign
                                </Button>
                              </chakra.td>
                            </chakra.tr>
                          ))}
                        </chakra.tbody>
                      </chakra.table>
                    </Box>
                  </Box>
                </Box>
              ) : owners && owners.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <Text color={textColor} fontSize="md">
                    No property owners found for this tenant.
                  </Text>
                  <Text color={labelColor} fontSize="sm" mt={2}>
                    Please add property owners to your tenant first.
                  </Text>
                </Box>
              ) : (
                <Box textAlign="center" py={6}>
                  <Text color={textColor} fontSize="md">
                    No property owner assigned to this station.
                  </Text>
                  <Text color={labelColor} fontSize="sm" mt={2}>
                    You can assign a property owner from the property owners
                    list.
                  </Text>
                  <Button
                    mt={6}
                    bg="#f57d36"
                    color="white"
                    size="md"
                    onClick={getOwners}
                    isLoading={assignLoading}
                    _hover={{
                      bg: "rgba(245, 125, 54, 0.8)",
                      boxShadow: "0 4px 12px rgba(245, 125, 54, 0.3)",
                      transform: "translateY(-2px)",
                    }}
                  >
                    Fetch Available Property Owners
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            _hover={{
              bg: "rgba(245, 125, 54, 0.1)",
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PropertyOwnerDetailsModal;
